import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Zap, Loader2, ExternalLink, ArrowRight, BadgeDollarSign, Info } from "lucide-react";
import { toast } from "sonner";

const CONDS = ["excellent", "good", "fair", "needs_work"];
const TITLES = ["clean", "rebuilt", "salvage", "lien"];
const F = "bg-slate-900 border-slate-800 mt-1.5";
const CONF = { high: "bg-emerald-500/20 text-emerald-300", medium: "bg-cyan-500/20 text-cyan-300", low: "bg-amber-500/20 text-amber-300" };

export default function InstantOffer({ onListInstead, onSkip }) {
  const [form, setForm] = useState({ year: 2019, make: "", model: "", trim: "", mileage: 80000, condition: "good", title_status: "clean", city: "", zip_code: "", runs_and_drives: true });
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const getOffer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/sales/instant-offer", { ...form, year: Number(form.year), mileage: Number(form.mileage) });
      setOffer(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not price your car right now"); } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-3xl p-8 rise rise-1" data-testid="instant-offer-step">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="font-mono text-xs tracking-widest uppercase text-emerald-400 mb-1 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" />Step 1 · Instant cash offer</p>
          <h2 className="font-display text-2xl font-bold">What's your car worth today?</h2>
          <p className="text-sm text-slate-400 mt-1">60-second estimate powered by MY VELOCITY AI, benchmarked to Cash For Cars–style wholesale offers.</p>
        </div>
        <button type="button" onClick={onSkip} data-testid="skip-offer-btn" className="text-xs text-slate-400 hover:text-white underline underline-offset-4">Skip — I know my price</button>
      </div>

      {!offer ? (
        <form onSubmit={getOffer} className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Year</Label><Input type="number" required value={form.year} onChange={(e) => upd("year", e.target.value)} data-testid="offer-year" className={F} /></div>
            <div><Label>Make</Label><Input required value={form.make} onChange={(e) => upd("make", e.target.value)} data-testid="offer-make" className={F} /></div>
            <div><Label>Model</Label><Input required value={form.model} onChange={(e) => upd("model", e.target.value)} data-testid="offer-model" className={F} /></div>
            <div><Label>Trim</Label><Input value={form.trim} onChange={(e) => upd("trim", e.target.value)} data-testid="offer-trim" className={F} /></div>
            <div><Label>Mileage</Label><Input type="number" required value={form.mileage} onChange={(e) => upd("mileage", e.target.value)} data-testid="offer-mileage" className={F} /></div>
            <div><Label>Condition</Label>
              <Select value={form.condition} onValueChange={(v) => upd("condition", v)}><SelectTrigger data-testid="offer-condition" className={F}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131722] border-slate-800">{CONDS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Title</Label>
              <Select value={form.title_status} onValueChange={(v) => upd("title_status", v)}><SelectTrigger data-testid="offer-title" className={F}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131722] border-slate-800">{TITLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>City</Label><Input required value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="offer-city" className={F} /></div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-slate-800">
            <span className="text-sm">Runs and drives</span><Switch checked={form.runs_and_drives} onCheckedChange={(v) => upd("runs_and_drives", v)} data-testid="offer-runs" />
          </div>
          <Button type="submit" disabled={busy} data-testid="get-offer-btn" className="w-full h-12 bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold btn-glow">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Pricing your car…</> : <><BadgeDollarSign className="w-4 h-4 mr-2" />Get my instant offer</>}
          </Button>
        </form>
      ) : (
        <div className="space-y-5" data-testid="offer-result">
          <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/5 border border-emerald-500/30">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Estimated instant cash offer</p>
                <p className="font-display text-5xl font-extrabold mt-1" data-testid="offer-amount">${Number(offer.offer).toLocaleString()}</p>
                <p className="text-sm text-slate-400 mt-1">Range ${Number(offer.low).toLocaleString()} – ${Number(offer.high).toLocaleString()} · private-party value ≈ ${Number(offer.private_party_value).toLocaleString()}</p>
              </div>
              <Badge className={`${CONF[offer.confidence] || CONF.low} border-0 font-mono text-[10px] uppercase`}>{offer.confidence} confidence · estimate</Badge>
            </div>
            <ul className="mt-4 grid sm:grid-cols-3 gap-2">{(offer.factors || []).map((f, i) => <li key={i} className="text-xs text-slate-300 px-3 py-2 rounded-lg bg-slate-950/40 border border-slate-800">{f}</li>)}</ul>
            <p className="text-xs text-slate-400 mt-3 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{offer.disclaimer}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href={offer.partner_url} target="_blank" rel="noreferrer" data-testid="cashforcars-btn"
              className="h-12 rounded-md flex items-center justify-center gap-2 bg-white text-slate-950 hover:bg-slate-100 font-semibold text-sm">
              <ExternalLink className="w-4 h-4" />Get a guaranteed offer from Cash For Cars
            </a>
            <Button onClick={() => onListInstead({ ...form, asking_price: Math.round(offer.private_party_value || offer.high * 1.3) })} data-testid="list-instead-btn"
              className="h-12 bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold">
              List it myself for more <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 text-center">Listing on MY VELOCITY typically nets ${Number(offer.private_party_value - offer.offer).toLocaleString()} more than the instant cash offer.</p>
          <button type="button" onClick={() => setOffer(null)} data-testid="offer-reprice-btn" className="text-xs text-slate-400 hover:text-white underline underline-offset-4 block mx-auto">Change details &amp; re-price</button>
        </div>
      )}
    </div>
  );
}
